import { Box, Button, Flex, Image, Text } from '@chakra-ui/react';
import ContributorCol from '../common/columns/ContributorCol';
import TeamsCol from '../common/columns/TeamsCol';
import LanguagesCol from '../common/columns/LanguagesCol';
import KnowledgeCol from '../common/columns/KnowledgeCol';
import MetricCol from '../common/columns/MetricCol';
import { useContriburotListGql } from '../common/graphql';
import { Paginate } from '../../appPaginate';
import { useContributorTableStore } from '../../hooks/useContributorTableStore';
import { useDashboardStore } from '../../hooks/useDashboardStore';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import NoContributorsImg from '../../shared/assets/contributor-table-no-contributors-img.svg';
import { useNavigate } from 'react-router-dom';
import { MetricSidebarContainer } from '../common/columns/MetricSidebarContainer';
import TeamAllocationCol from '../common/columns/TeamAllocation';
import GoalsCol from '../../TeamTable/columns/GoalsCol';
import { useGoalStatusSummaryGql } from '../../TeamTable/graphql';
import KeyResultsCol from '../../TeamTable/columns/keyResultsCol';
import { getRowHeight } from '../common/utils/contributorList.utils';
import {
  Contributor,
  ContributorListProps,
  ContributorTableRow,
  MetricTooltip,
} from '../common/types/contributorTableTypes';

export const ContributorList = ({
  compareTo,
  searchTerm,
  metricTooltips,
  repoState,
  teamState,
  jobRoleState,
  languageState,
  activityFilter,
  selectedMetrics,
  orderBy,
  orderColumn,
  metricFilterState,
  setOrderBy,
  setOrderColumn,
  customComponentRenderers = [],
  rowHeight,
  hiddenColumns,
  height = '66vh',
  startDate,
  endDate,
  metricList,
  setMetricFilterState,
  showContributorPercentiles,
}: ContributorListProps) => {
  const navigate = useNavigate();
  const [currentPage, setCurrentPage] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const { selectedTeam, selected } = useDashboardStore();
  const boxRef = useRef<HTMLDivElement | null>(null);
  const {
    selectedContributors,
    setSelectedContributors,
    setIsContributorsEmpty,
    isContributorsEmpty,
  } = useContributorTableStore();

  const { data: contributorList, isFetching: contributorListLoading } =
    useContriburotListGql(
      {
        searchTerm: searchTerm.length < 3 ? '' : searchTerm.trim(),
        repoState,
        languageState,
        jobRoleState,
        activityFilter: activityFilter,
        metricFilters: [],
      },
      teamState,
      startDate || selected.startDate,
      endDate || selected.endDate,
      selected.sprintId,
      orderColumn || metricFilterState.length > 0 ? selectedContributors : [],
      orderColumn || metricFilterState.length > 0 ? 0 : currentPage,
      10
    );

  const { data: goalStatusSummary, isFetching: goalStatusSummaryLoading } =
    useGoalStatusSummaryGql(
      'CONTRIBUTOR',
      contributorList?.data?.map(
        (item: ContributorTableRow) => item?.contributors?.email
      ) || []
    );

  const contributors =
    contributorList?.data?.map(
      (item: ContributorTableRow) => item?.contributors
    ) || [];

  const teams =
    contributorList?.data?.map((item: ContributorTableRow) => item?.teams) ||
    [];

  const languages =
    contributorList?.data?.map(
      (item: ContributorTableRow) => item?.languages
    ) || [];

  const knowledgeArea =
    contributorList?.data?.map(
      (item: ContributorTableRow) => item?.knowledgeArea
    ) || [];
  const teamAllocation =
    contributorList?.data?.map(
      (item: ContributorTableRow) => item?.teamAllocationSummary
    ) || [];

  const handlePageClick = useCallback(
    (page: number) => {
      setCurrentPage(page - 1);
    },
    [currentPage]
  );
  const scrollToTop = () => {
    if (boxRef.current) {
      boxRef.current.scrollTo({
        top: 0,
        behavior: 'auto',
      });
    }
  };
  // keep page as 0 on change of filters/search and date change
  useEffect(() => {
    scrollToTop();
    setCurrentPage(0);
    setIsContributorsEmpty(false);
  }, [
    searchTerm,
    repoState,
    teamState,
    metricFilterState,
    languageState,
    orderColumn,
    selected,
    activityFilter,
    showContributorPercentiles,
  ]);

  // when a column where sort was applied is removed by user. set orderColumn to '' and set setSelectedContributors to []
  useEffect(() => {
    if (
      metricFilterState.length > 0 &&
      selectedMetrics.length > 0 &&
      setMetricFilterState
    ) {
      const metrics = selectedMetrics.map((metric) => metric.metricKey);
      setMetricFilterState((prev: any) =>
        prev.filter((metric: any) => metrics.includes(metric.metricName))
      );
    }
    if (
      orderColumn &&
      !selectedMetrics.map((metric) => metric.metricKey).includes(orderColumn)
    ) {
      setOrderColumn('');
      setOrderBy('');
      setSelectedContributors([]);
    }
  }, [selectedMetrics]);

  useEffect(() => {
    if (
      contributorList?.totalPages &&
      orderColumn === '' &&
      metricFilterState.length === 0
    ) {
      setSelectedContributors([]);
      setTotalPages(contributorList?.totalPages ?? 1);
    }

    if (contributorList?.data?.length === 0) {
      setOrderColumn('');
      setOrderBy('');
      setSelectedContributors([]);
      setIsContributorsEmpty(true);
    }
  }, [contributorList]);

  if (isContributorsEmpty) {
    return (
      <Flex
        mx={'auto'}
        align="center"
        justify="center"
        direction="column"
        mt={100}
      >
        <Image src={NoContributorsImg} mb={4} />
        <Text fontSize="4xl" fontWeight="medium" my={2}>
          No contributors found!
        </Text>
      </Flex>
    );
  }

  return (
    <Flex direction="column">
      <Flex
        mt={3}
        overflowX={'scroll'}
        ref={boxRef}
        overflowY={'auto'}
        border="1px solid #E0E0E0"
        height={height}
        width="100%"
        borderRadius="8px"
        position="relative"
      >
        <ContributorCol
          contributors={contributors}
          loading={contributorListLoading}
          rowHeight={rowHeight || getRowHeight(selectedMetrics)}
        />

        {!hiddenColumns?.includes('Teams') && (
          <TeamsCol
            teams={teams}
            loading={contributorListLoading}
            rowHeight={rowHeight || getRowHeight(selectedMetrics)}
          />
        )}

        {!hiddenColumns?.includes('Languages') && (
          <LanguagesCol
            languages={languages}
            loading={contributorListLoading}
            rowHeight={rowHeight || getRowHeight(selectedMetrics)}
          />
        )}

        {!hiddenColumns?.includes('Knowledge Area') && (
          <KnowledgeCol
            knowledgeArea={knowledgeArea}
            loading={contributorListLoading}
            selectedMetrics={selectedMetrics}
            rowHeight={rowHeight || getRowHeight(selectedMetrics)}
          />
        )}
        {!hiddenColumns?.includes('Team Allocation') && (
          <TeamAllocationCol
            teamAllocation={teamAllocation}
            loading={contributorListLoading}
            selectedMetrics={selectedMetrics}
            rowHeight={rowHeight || getRowHeight(selectedMetrics)}
          />
        )}

        {customComponentRenderers.map((renderer, index) => (
          <React.Fragment key={`custom-component-${index}`}>
            {renderer(
              contributors
                ?.map((contributor: Contributor) => contributor?.email)
                .filter(Boolean)
            )}
          </React.Fragment>
        ))}

        {selectedMetrics.map((item) => {
          if (item.metricKey === 'GOALS') {
            return (
              <GoalsCol
                goalScope="CONTRIBUTOR"
                associatedIds={contributors?.map(
                  (item: Contributor) => item?.email
                )}
                goalsTeamDataLoading={contributorListLoading}
                goalStatusSummary={goalStatusSummary || []}
                goalStatusSummaryLoading={goalStatusSummaryLoading}
                expanded={['']}
                hasRightBorder={
                  selectedMetrics[selectedMetrics.length - 1].metricKey ===
                  item.metricKey
                }
              />
            );
          }
          if (item.metricKey === 'KEY_RESULTS') {
            return (
              <KeyResultsCol
                goalScope="CONTRIBUTOR"
                associatedIds={contributors?.map(
                  (item: Contributor) => item?.email
                )}
                goalsTeamDataLoading={contributorListLoading}
                goalStatusSummary={goalStatusSummary || []}
                goalStatusSummaryLoading={goalStatusSummaryLoading}
                expanded={['']}
                hasRightBorder={
                  selectedMetrics[selectedMetrics.length - 1].metricKey ===
                  item.metricKey
                }
                rowHeight={rowHeight || getRowHeight(selectedMetrics)}
              />
            );
          }
          return (
            <MetricCol
              key={item.metricKey}
              metricTooltip={
                metricTooltips?.find(
                  (metricItem: MetricTooltip) =>
                    metricItem?.metric === item?.metricKey
                ) || undefined
              }
              metricList={metricList || []}
              selectedMetrics={selectedMetrics}
              compareTo={compareTo}
              orderColumn={orderColumn}
              searchTerm={searchTerm}
              jobRoleState={jobRoleState}
              repoState={repoState}
              languageState={languageState}
              activityFilter={activityFilter}
              setOrderColumn={setOrderColumn}
              contributors={contributors?.map(
                (item: Contributor) => item?.email
              )}
              title={item.metricTitle}
              metricKey={item.metricKey}
              currentPage={currentPage}
              setCurrentPage={setCurrentPage}
              setTotalPages={setTotalPages}
              scrollToTop={scrollToTop}
              loading={contributorListLoading}
              initialOrderBy={orderColumn === item.metricKey ? orderBy : ''}
              setOrderByUrl={setOrderBy}
              rowHeight={rowHeight || getRowHeight(selectedMetrics)}
              startDate={startDate}
              endDate={endDate}
              metricFilterState={metricFilterState}
              teamState={teamState}
              showContributorPercentiles={showContributorPercentiles}
            />
          );
        })}
      </Flex>

      <Box marginLeft="-20px">
        <Paginate
          currentPage={currentPage + 1}
          totalPageCount={totalPages}
          pageSize={10}
          onPageChange={(page) => {
            handlePageClick(page as number);
            scrollToTop();
          }}
        />
      </Box>
      <MetricSidebarContainer />
    </Flex>
  );
};

export default ContributorList;
